/**
 * 🔓 InvoiceVerify — Page publique d'authentification facture
 * ============================================================
 *
 * Page sans authentification, accessible via le QR code des PDF facture.
 * Affiche un badge "Facture authentique" + données minimales (numéro, montant,
 * émetteur, date, hash). Conçue pour les directions financières/comptables.
 *
 * Route : /v/:invoiceId
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ShieldCheck, AlertTriangle, Loader2, Receipt } from 'lucide-react';
import { getBackendUrl } from '../config/environment';

const BACKEND_URL = getBackendUrl();

const T = {
  ink: '#1C1147',
  inkSoft: '#3A2E6E',
  gold: '#D4AF37',
  goldDeep: '#B7901F',
  goldLight: '#F3D060',
  ivory: '#FBF9F3',
  ivoryDeep: '#F4F1E8',
  border: '#E8E5DE',
  text: '#374151',
  muted: '#6B7280',
  green: '#48825A',
  greenSoft: '#9FE5B5',
};

const formatEUR = (n) =>
  typeof n === 'number'
    ? n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
    : '—';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
};

export default function InvoiceVerify() {
  const { invoiceId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/invoices/${invoiceId}/verify`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (active) setData(json);
      } catch (e) {
        if (active) setError(e.message || 'Erreur');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [invoiceId]);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-10"
      style={{
        background: `linear-gradient(135deg, ${T.ivory} 0%, ${T.ivoryDeep} 100%)`,
        fontFamily: 'Helvetica, Arial, sans-serif',
      }}
      data-testid="invoice-verify-page"
    >
      {/* Header brand */}
      <div className="mb-6 text-center">
        <div
          className="text-[10px] uppercase tracking-[0.28em] font-bold mb-1"
          style={{ color: T.goldDeep }}
        >
          AMSY E-COM · COACH DIA
        </div>
        <h1
          className="font-serif text-3xl font-bold"
          style={{ color: T.ink }}
        >
          Vérification d'authenticité
        </h1>
      </div>

      {loading && (
        <div className="flex items-center gap-2" style={{ color: T.muted }}>
          <Loader2 className="w-5 h-5 animate-spin" /> Vérification en cours…
        </div>
      )}

      {!loading && error && (
        <div
          className="rounded-2xl p-8 max-w-md text-center"
          style={{
            background: '#FFF7ED',
            border: '1px solid #FED7AA',
            color: '#9A3412',
          }}
          data-testid="verify-error"
        >
          <AlertTriangle className="w-10 h-10 mx-auto mb-3" />
          <div className="font-bold text-lg mb-1">Erreur de vérification</div>
          <div className="text-sm">{error}</div>
        </div>
      )}

      {!loading && data && data.valid === false && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl p-8 max-w-md text-center"
          style={{
            background: '#FFFFFF',
            border: `2px solid #DC2626`,
          }}
          data-testid="verify-invalid"
        >
          <AlertTriangle className="w-12 h-12 mx-auto mb-3" style={{ color: '#DC2626' }} />
          <div className="font-bold text-xl mb-2" style={{ color: '#DC2626' }}>
            Facture introuvable
          </div>
          <div className="text-sm" style={{ color: T.muted }}>
            {data.message || "Aucune facture ne correspond à cet identifiant."}
          </div>
        </motion.div>
      )}

      {!loading && data && data.valid && (
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-3xl p-8 max-w-xl w-full"
          style={{
            background: '#FFFFFF',
            border: `1px solid ${T.gold}55`,
            boxShadow: `0 16px 48px -16px ${T.ink}33, 0 0 0 1px ${T.gold}22 inset`,
          }}
          data-testid="verify-valid"
        >
          {/* Badge auth */}
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 220 }}
            className="flex flex-col items-center text-center mb-6"
          >
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mb-3"
              style={{
                background: `linear-gradient(135deg, ${T.green}, ${T.greenSoft})`,
                boxShadow: `0 0 0 8px ${T.green}22, 0 8px 24px -6px ${T.green}aa`,
              }}
            >
              <CheckCircle2 className="w-11 h-11 text-white" strokeWidth={2.5} />
            </div>
            <div
              className="font-serif text-2xl font-bold"
              style={{ color: T.ink }}
            >
              Facture authentique
            </div>
            <div className="text-sm mt-1 max-w-md" style={{ color: T.muted }}>
              {data.verification_message}
            </div>
          </motion.div>

          {/* Détails */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Numéro', value: data.invoice_number, highlight: true },
              { label: 'Émetteur', value: `${data.issuer} SAS` },
              { label: 'Produit', value: data.product },
              { label: 'Date d\'émission', value: formatDate(data.issued_at) },
              { label: 'Montant TTC', value: formatEUR(data.amount_ttc), highlight: true },
              { label: 'Statut', value: data.status === 'paid' ? '✓ Payée' : data.status, accent: data.status === 'paid' },
            ].map((row) => (
              <div
                key={row.label}
                className="rounded-lg p-3"
                style={{
                  background: row.highlight ? T.ivory : T.ivoryDeep,
                  border: `1px solid ${T.border}`,
                }}
              >
                <div
                  className="text-[9.5px] uppercase tracking-[0.16em] font-bold mb-1"
                  style={{ color: T.goldDeep }}
                >
                  {row.label}
                </div>
                <div
                  className="font-semibold text-sm"
                  style={{
                    color: row.accent ? T.green : T.ink,
                    fontFamily: row.label === 'Montant TTC' ? 'serif' : 'inherit',
                  }}
                >
                  {row.value}
                </div>
              </div>
            ))}
          </div>

          {/* Hash intégrité */}
          {data.integrity_hash && (
            <div
              className="mt-4 rounded-lg p-3 flex items-start gap-2"
              style={{
                background: T.ivoryDeep,
                border: `1px solid ${T.gold}33`,
              }}
            >
              <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: T.goldDeep }} />
              <div className="flex-1 min-w-0">
                <div
                  className="text-[9.5px] uppercase tracking-[0.16em] font-bold mb-1"
                  style={{ color: T.goldDeep }}
                >
                  Empreinte d'intégrité SHA-256
                </div>
                <div
                  className="text-[10.5px] break-all"
                  style={{
                    color: T.inkSoft,
                    fontFamily: 'Courier, monospace',
                  }}
                >
                  {data.integrity_hash}
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div
            className="mt-6 pt-4 border-t text-center text-[11px]"
            style={{ borderColor: T.border, color: T.muted }}
          >
            <Receipt className="w-3 h-3 inline mr-1" />
            Vérification publique · COACH DIA Audit-Ready
          </div>
        </motion.div>
      )}
    </div>
  );
}
